import { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown } from "react-icons/fi";

type MultiSelectOption = {
  label: string;
  value: number;
};

export default function MultiSelectDropdown({
  label,
  options,
  selectedValues,
  onChange,
  placeholder,
  emptyText,
  disabled = false,
}: {
  label: string;
  options: MultiSelectOption[];
  selectedValues: number[];
  onChange: (values: number[]) => void;
  placeholder: string;
  emptyText?: string;
  disabled?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
      setSearchTerm("");
    }
  }, [disabled]);

  const selectedLabels = useMemo(
    () =>
      options
        .filter((option) => selectedValues.includes(option.value))
        .map((option) => option.label),
    [options, selectedValues],
  );

  const displayText =
    selectedLabels.length === 0
      ? placeholder
      : selectedLabels.length <= 2
        ? selectedLabels.join(", ")
        : `${selectedLabels.slice(0, 2).join(", ")} +${selectedLabels.length - 2}`;

  const filteredOptions = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return options;
    }

    return options.filter((option) =>
      option.label.toLowerCase().includes(normalizedSearch),
    );
  }, [options, searchTerm]);

  return (
    <div className="relative" ref={containerRef}>
      <label className="mb-2 block text-sm font-medium text-gray-700">
        {label}
      </label>
      <button
        type="button"
        className={[
          "input-primary w-full flex items-center justify-between text-left",
          disabled ? "cursor-not-allowed bg-gray-100 text-gray-400" : "",
        ].join(" ")}
        onClick={() => {
          if (disabled) return;
          setIsOpen((current) => {
            const nextIsOpen = !current;
            if (!nextIsOpen) {
              setSearchTerm("");
            }
            return nextIsOpen;
          });
        }}
        disabled={disabled}
      >
        <span className={selectedLabels.length === 0 ? "text-gray-400" : ""}>
          {displayText}
        </span>
        <FiChevronDown
          className={[
            "h-4 w-4 flex-shrink-0 transition-transform",
            isOpen ? "rotate-180" : "",
          ].join(" ")}
        />
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-2 w-full rounded-xl border border-gray-200 bg-white shadow-lg">
          <div className="max-h-56 overflow-y-auto p-3">
            <input
              type="text"
              className="input-primary mb-3"
              placeholder={`Search ${label.toLowerCase()}`}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            {filteredOptions.length === 0 ? (
              <div className="text-sm text-gray-500">
                {options.length === 0
                  ? emptyText || "No options available."
                  : "No matching options found."}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {filteredOptions.map((option) => {
                  const checked = selectedValues.includes(option.value);

                  return (
                    <label
                      key={option.value}
                      className="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(event) =>
                          onChange(
                            event.target.checked
                              ? [...selectedValues, option.value]
                              : selectedValues.filter(
                                  (value) => value !== option.value,
                                ),
                          )
                        }
                      />
                      <span>{option.label}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
